/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

/* eslint-disable no-nested-ternary */
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styles from 'modules/dashboard/components/tab-container/tab-container.module.scss';
import { selectEmbedToken, selectHotelName, selectTabList } from 'modules/dashboard/selectors';
import { EXPORT_OPTIONS, REPORT_TYPE } from 'modules/dashboard/constants';
import VisualGrid from 'modules/dashboard/components/tab-container/visual-grid';
import VisualCard from 'modules/dashboard/components/tab-container/visual-card';
import ExportToCSV, {
  getPbiExportData,
} from 'modules/dashboard/components/tab-container/export-visual-data/export-csv';
import { AssistantIcon } from 'modules/common/components';
import { selectAuthUser } from 'modules/common/auth/selectors';
import {
  selectActivePaceTab,
  selectHotelFocusOn,
  selectPaceAlignmentToggle,
  selectPaceCumulativeView,
  selectPaceLagAligned,
  selectPaceSymmetricComparisonToggle,
  selectPaceTimeSegmentCumulativeLagAlignedTabularVisuals,
  selectPaceTimeSegmentCumulativeTabularVisuals,
  selectPaceTimeSegmentLagAlignedTabularVisuals,
  selectPaceTimeSegmentTabularFilterList,
  selectPaceTimeSegmentTabularVisuals,
  selectRevenueIsDisplayPS,
} from '../../../../../pace-tabs/selectors';
import { paceActions } from '../../../../../pace-tabs/slice';
import { isEmpty } from '../../../../../../../../common/helpers/object';
/**
 * Pace Time Segment tabular visual report with filters
 */
const PaceTimeSegmentTabularWidget = ({ setIsSegmentLoaded }) => {
  const dispatch = useDispatch();
  // States are obtained from redux store. Refer each selector function for details.
  const tabularFilterList = useSelector(selectPaceTimeSegmentTabularFilterList);
  const alignmentToggle = useSelector(selectPaceAlignmentToggle);
  const symmetricComparisonToggle = useSelector(selectPaceSymmetricComparisonToggle);
  const tokenDetails = useSelector(selectEmbedToken);
  const paceLagAligned = useSelector(selectPaceLagAligned);
  const paceCumulative = useSelector(selectPaceCumulativeView);
  const revenueIsDisplay = useSelector(selectRevenueIsDisplayPS);
  const paceTimeVisuals = useSelector(selectPaceTimeSegmentTabularVisuals);
  const paceTimeLagAlignedVisuals = useSelector(selectPaceTimeSegmentLagAlignedTabularVisuals);
  const TAB_VALUE = useSelector(selectTabList);
  const paceTimeAbsoluteCumulativeVisuals = useSelector(
    selectPaceTimeSegmentCumulativeTabularVisuals
  );
  const paceTimeCumulativeLagAlignedVisuals = useSelector(
    selectPaceTimeSegmentCumulativeLagAlignedTabularVisuals
  );
  const activeTab = useSelector(selectActivePaceTab);
  const hotelFocusOn = useSelector(selectHotelFocusOn);
  const currentUser = useSelector(selectAuthUser);
  const selectedHotel = useSelector(selectHotelName);
  //
  const [tabularCombined, setTabularCombined] = useState({});
  const [isTabularCombinedRendered, setIsTabularCombinedRendered] = useState(false);
  const [isVisualProcessing, setIsVisualProcessing] = useState(false);
  const [exportType, setExportType] = useState(EXPORT_OPTIONS.DATA_EXPORT);
  //
  const trendFilters = `Alignment : , ${
    alignmentToggle ? 'On' : 'Off'
  }\r\nSymmetric Comparison : , ${symmetricComparisonToggle ? 'On' : 'Off'}\r\n`;
  // To get visual details
  const getVisual = (index) => {
    let found;
    if (paceCumulative && !paceLagAligned) {
      found = paceTimeAbsoluteCumulativeVisuals?.find((visual) => visual?.order === index);
    } else if (paceLagAligned && !paceCumulative) {
      found = paceTimeLagAlignedVisuals?.find((visual) => visual?.order === index);
    } else if (paceLagAligned && paceCumulative) {
      found = paceTimeCumulativeLagAlignedVisuals?.find((visual) => visual?.order === index);
    } else {
      found = paceTimeVisuals?.find((visual) => visual?.order === index);
    }
    return found;
  };
  // To get visuals based on the configuration
  const getWidget = (
    index,
    cssClassName,
    setPBIReport = () => {},
    setIsLoaded = () => {},
    setIsRendered = () => {},
    displayFunction = () => {},
    filter = tabularFilterList
  ) => {
    const found = getVisual(index);
    return (
      found && (
        <VisualCard
          cssClassName={cssClassName}
          accessToken={tokenDetails?.accessToken}
          embedUrl={found?.embedUrl}
          id={found?.reportId}
          visualName={found?.name}
          pageName={found?.pageName}
          pageDisplayName={found?.pageDisplayName}
          reportName={found?.reportName}
          filters={filter}
          setPBIReport={(r) => setPBIReport(r)}
          setIsLoaded={(r) => setIsLoaded(r)}
          setIsRendered={(d) => setIsRendered(d)}
          tab={TAB_VALUE.PACE_TEST}
          dispatch={dispatch}
          displayFunction={displayFunction}
        />
      )
    );
  };
  // To get visual names based on visual order
  const getVisualName = (order) => getVisual(order)?.name;
  //
  const getExcelExportData = async () => {
    const data = await getPbiExportData({
      isLoaded: isTabularCombinedRendered,
      fileName:
        paceCumulative && paceLagAligned
          ? 'PTS_Cumulative_Lag_Aligned'
          : paceCumulative && !paceLagAligned
          ? 'PTS_Cumulative'
          : paceLagAligned && !paceCumulative
          ? 'PTS_Lag_Aligned'
          : 'PTS_Daily',
      currentUser,
      selectedHotel,
      report: tabularCombined,
    });
    return data;
  };
  //
  useEffect(() => {
    setIsTabularCombinedRendered(false);
  }, [paceCumulative, paceLagAligned]);
  //
  return (
    !isEmpty(tabularFilterList) && (
      <VisualGrid
        isDisplay={revenueIsDisplay}
        visualName={getVisualName(0)}
        visual={getWidget(
          0,
          styles.tabularVisual,
          setTabularCombined,
          setIsSegmentLoaded,
          setIsTabularCombinedRendered,
          paceActions.setRevenueIsDisplayPS
        )}
        onClick={() => {
          dispatch(paceActions.setRevenueIsDisplayPS(!revenueIsDisplay));
        }}
        information={
          !paceLagAligned
            ? 'Booking dates without data are not shown on the visual'
            : 'Lags without data are not shown on the visual'
        }
        toggleFragment={
          <ExportToCSV
            exportExcel
            report={tabularCombined}
            isLoaded={isTabularCombinedRendered}
            fileName={
              paceCumulative && paceLagAligned
                ? 'PTS_Cumulative_Lag_Aligned'
                : paceCumulative && !paceLagAligned
                ? 'PTS_Cumulative'
                : paceLagAligned && !paceCumulative
                ? 'PTS_Lag_Aligned'
                : 'PTS_Daily'
            }
            otherFilters={trendFilters}
          />
        }
        isWorkbookHotelComparison={activeTab === REPORT_TYPE.PACE_HOTEL_WIDGET}
        comparisonHotel={hotelFocusOn}
        displayHotelName
        interpreterFragment={
          <AssistantIcon
            visualName={getVisualName(0)}
            report={tabularCombined}
            isProcessing={isVisualProcessing}
            setIsProcessing={setIsVisualProcessing}
            disabled={!isTabularCombinedRendered || isVisualProcessing || !revenueIsDisplay}
            getExcelExportData={getExcelExportData}
            exportType={exportType}
            setExportType={setExportType}
          />
        }
      />
    )
  );
};
//
export default PaceTimeSegmentTabularWidget;
